import Paper from "@material-ui/core/Paper";
import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableFooter from "@material-ui/core/TableFooter";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import { userSignOut } from "actions/Auth";
import ResponsiveTableBody from "components/ResponsiveTableBody/ResponsiveTableBody";
import SearchField from "components/SearchField/SearchField";
import React, { useEffect, useState } from "react";
import { MdHelpOutline } from "react-icons/md";
import { useDispatch } from "react-redux";
import { getAxios } from "../../api/Axios";
import { groupApi, missionApi } from "../../api/Urls";
import CardBox from "../../components/CardBox";
import CircularProgress from "../../components/CircularProgress";
import CustomActions from "../../components/CustomPagination/CustomActions";
import CustomPagination from "../../components/CustomPagination/CustomPagination";
import AddMission from "./AddMission";
import useWindowSize from "../../hooks/useWindowsSize";
import { useTranslation } from "react-i18next";
import Tour from "components/Tour";
import { TbMail } from "react-icons/tb";
import moment from "jalali-moment";
import toFarsiNumber from "util/toFarsiNumber";
import MissionDetails from "./MissionDetails";
import { useSnackbar } from "notistack";
import classes2 from "./MissionComponent.module.scss";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    marginTop: theme.spacing(3),
  },
  table: {
    minWidth: 850,
    overflow: "hidden",
  },
  tableWrapper: {
    overflowX: "auto",
  },
  actions: {
    display: "flex",
  },
  tableCellLeftAlignment: {
    textAlign: "right !important",
  },
  location: {
    color: "#ffb200",
    marginRight: "1rem",
  },
  phone: {
    color: "blue",
    marginRight: "1rem",
  },
  delete: {
    color: "red",
  },
  tableHeaderContainer: {
    display: "flex",
    justifyContent: "space-between",
  },
  tableHeaderContainerLtr: {
    direction: "rtl",
    display: "flex",
    justifyContent: "space-between",
  },
  helpContainer: {
    display: "flex",
    alignItems: "center",
  },
  helpIcon: {
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
  },
  detailBackground: {
    backgroundColor: "rgba(231, 236, 244, 0.9)",
    borderRadius: "0.6em",
    padding: "0.3em 0.6em ",
    cursor: "pointer",
  },
  iconDirectionLtr: {
    flexDirection: "row",
  },
}));

export default function MissionComponent() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const windowSize = useWindowSize();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [data, setData] = useState([]);
  const [hardData, setHardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchVal, setSearchVal] = useState("");
  const [openDetails, setOpenDetails] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailData, setDetailData] = useState([]);
  const [groupData, setGroupData] = useState([]);
  const [userData, setUserData] = useState([]);
  const [groupReceivers, setGroupReceivers] = useState([]);
  const [refreshData, setRefreshData] = useState(false);
  const [showAgainHelp, setShowAgainHelp] = useState({
    isChange: false,
    value: "",
  });

  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, data.length - page * rowsPerPage);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    getAxios(missionApi)
      .then((res) => {
        setData(res.data.mission);
        setHardData(res.data.mission);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        if (err?.response) {
          enqueueSnackbar(err.response.data.message, {
            variant: "error",
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "left",
            },
            style: { direction: i18n.language === "en" ? "ltr" : "rtl" },
          });
        } else {
          console.log(err);
        }
        if (err?.response?.status === 401) {
          dispatch(userSignOut());
        }
      });
  }, [refreshData]);

  useEffect(() => {
    getAxios(groupApi)
      .then((res) => {
        setGroupData(res.data);
        setGroupReceivers(res?.data.slice().fill(false));
      })
      .catch((err) => {
        if (err?.response) {
          enqueueSnackbar(err.response.data.message, {
            variant: "error",
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "left",
            },
            style: { direction: i18n.language === "en" ? "ltr" : "rtl" },
          });
        } else {
          console.log(err);
        }
        if (err?.response?.status === 401) {
          dispatch(userSignOut());
        }
      });
  }, []);

  /**
   * Convert just english digits to persian
   * @author Khalili
   * @param String $text
   * @returns String
   */
  const toPersianDigits = (text) => {
    var id = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
    return text.replace(/[0-9]/g, function (w) {
      return id[+w];
    });
  };

  /**
   * Filter users(subscribers) list by search field
   * @author Khalili
   * @returns Array
   */
  useEffect(() => {
    setData(
      hardData.filter((item) => {
        return (
          toPersianDigits(item.subject).includes(toPersianDigits(searchVal)) ||
          toPersianDigits(item.message).includes(toPersianDigits(searchVal))
        );
      })
    );
  }, [searchVal]);

  const handleShowAgain = (value) => {
    setShowAgainHelp({ isChange: !showAgainHelp.isChange, value: value });
  };

  const handleRequestCloseDetails = () => {
    setOpenDetails(false);
  };

  return (
    <>
      <Tour page="missions" showAgainHelp={showAgainHelp} />
      <div
        className={`row animated slideInUpTiny animation-duration-3 ${
          i18n.dir() === "rtl" ? "uptin-table" : "uptin-table-ltr"
        }`}
      >
        <CardBox styleName="col-12" cardStyle="p-0" headerOutside>
          <>
            <div
              className={
                i18n.dir() === "rtl"
                  ? classes2.tableHeaderContainer
                  : classes2.tableHeaderContainerLtr
              }
            >
              <span
                className={`${
                  i18n.dir() === "rtl"
                    ? classes2.addContainer
                    : classes2.addContainerLtr
                } missions-step-one`}
              >
                <AddMission
                  setRefreshData={setRefreshData}
                  groupData={groupData}
                  setGroupData={setGroupData}
                  groupReceivers={groupReceivers}
                  setGroupReceivers={setGroupReceivers}
                />
              </span>
              <div className={classes2.helpContainer}>
                <span
                  onClick={() => handleShowAgain("missions")}
                  className={classes.helpIcon}
                >
                  <MdHelpOutline fontSize={20} />
                </span>
                <SearchField
                  width={"20em"}
                  placeholder={t("missions.search_in_missions")}
                  value={searchVal}
                  setValue={setSearchVal}
                />
              </div>
            </div>
            <TableContainer
              style={{
                borderBottomRightRadius: "1.5rem",
                borderBottomLeftRadius: "1.5rem",
                overflow: "hidden",
                direction: i18n.dir() === "rtl" ? "rtl" : "ltr",
              }}
              component={Paper}
            >
              {loading ? (
                <CircularProgress />
              ) : data.length > 0 ? (
                <>
                  {windowSize.width > 430 ? (
                    <Table
                      className={classes.table}
                      aria-label="custom pagination table"
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell
                            className={`${
                              i18n.dir() === "ltr"
                                ? classes.tableCellLeftAlignment
                                : ""
                            } text-black-50`}
                          >
                            {t("missions.title")}
                          </TableCell>
                          <TableCell
                            className={`${
                              i18n.dir() === "ltr"
                                ? classes.tableCellLeftAlignment
                                : ""
                            } text-black-50 my-other-step`}
                          >
                            {t("missions.date")}
                          </TableCell>
                          <TableCell className="text-black-50"></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(rowsPerPage > 0
                          ? data.slice(
                              page * rowsPerPage,
                              page * rowsPerPage + rowsPerPage
                            )
                          : data
                        ).map((n, index) => {
                          return (
                            <TableRow key={n.pk}>
                              <TableCell
                                className={
                                  i18n.dir() === "ltr"
                                    ? classes.tableCellLeftAlignment
                                    : ""
                                }
                              >
                                {n.subject}
                              </TableCell>
                              <TableCell
                                className={
                                  i18n.dir() === "ltr"
                                    ? classes.tableCellLeftAlignment
                                    : ""
                                }
                              >
                                {i18n.language === "fa"
                                  ? toFarsiNumber(
                                      moment(n.created_at, "YYYY/MM/DD")
                                        .locale("fa")
                                        .format("YYYY/MM/DD")
                                    )
                                  : moment(n.created_at, "YYYY/MM/DD")
                                      .locale("en")
                                      .format("YYYY/MM/DD")}
                              </TableCell>
                              <TableCell>
                                <span
                                  style={{ cursor: "pointer", display: "none" }}
                                  className={`${classes.detailBackground} missions-step-two`}
                                  // onClick={() => getNotificationDetails(n.uuid)}
                                >
                                  {t("missions.details")}
                                </span>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        {emptyRows > 0 && (
                          <TableRow style={{ height: 48 * emptyRows }}>
                            <TableCell colSpan={6} />
                          </TableRow>
                        )}
                      </TableBody>
                      <TableFooter>
                        <TableRow
                          style={{
                            overflow: "hidden",
                            position: "relative",
                            width: "100%",
                          }}
                        >
                          <CustomActions
                            page={page}
                            setPage={setPage}
                            dataLength={data.length}
                            rowsPerPage={rowsPerPage}
                          />
                          <CustomPagination setRowsPerPage={setRowsPerPage} />

                          <TablePagination
                            rowsPerPageOptions={[
                              5,
                              10,
                              20,
                              30,
                              { label: "All", value: -1 },
                            ]}
                            // colSpan={5}
                            count={data.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            SelectProps={{
                              inputProps: {
                                "aria-label": "Rows per page",
                              },
                              native: true,
                            }}
                            labelRowsPerPage={t("missions.rows-per-page")}
                            onChangePage={handleChangePage}
                            onChangeRowsPerPage={handleChangeRowsPerPage}
                            // ActionsComponent={TablePaginationActions}
                          />
                        </TableRow>
                      </TableFooter>
                    </Table>
                  ) : (
                    <Table
                      style={{
                        border: "2px solid #e7ecf4",
                        borderRadius: "1.6rem",
                      }}
                      aria-label="simple table"
                    >
                      <TableBody>
                        {(rowsPerPage > 0
                          ? data.slice(
                              page * rowsPerPage,
                              page * rowsPerPage + rowsPerPage
                            )
                          : data
                        ).map((row, index) => (
                          <ResponsiveTableBody
                            row={row}
                            tableKeys={[
                              t("missions.title"),
                              t("missions.date"),
                              "",
                            ]}
                            tableValues={["subject", "created_at", ""]}
                            actions={
                              <span
                                style={{ cursor: "pointer", display: "none" }}
                                className={`${classes.detailBackground} missions-step-two`}
                                // onClick={() => getNotificationDetails(row.uuid)}
                              >
                                {t("missions.details")}
                              </span>
                            }
                          />
                        ))}
                      </TableBody>
                      <TableFooter>
                        <TableRow
                          style={{
                            overflow: "hidden",
                            position: "relative",
                            width: "100%",
                          }}
                        >
                          <TableCell colSpan={3} align="center">
                            <CustomActions
                              page={page}
                              setPage={setPage}
                              dataLength={data.length}
                              rowsPerPage={rowsPerPage}
                            />
                            <CustomPagination setRowsPerPage={setRowsPerPage} />
                            <TablePagination
                              rowsPerPageOptions={[
                                5,
                                10,
                                20,
                                30,
                                { label: "All", value: -1 },
                              ]}
                              // colSpan={3}
                              count={data.length}
                              rowsPerPage={rowsPerPage}
                              page={page}
                              SelectProps={{
                                inputProps: {
                                  "aria-label": "Rows per page",
                                },
                                native: true,
                              }}
                              labelRowsPerPage={t("missions.rows_per_page")}
                              onChangePage={handleChangePage}
                              onChangeRowsPerPage={handleChangeRowsPerPage}
                              // ActionsComponent={TablePaginationActions}
                            />
                          </TableCell>
                        </TableRow>
                      </TableFooter>
                    </Table>
                  )}
                </>
              ) : (
                <div className="no-users">
                  <div style={{ padding: "4rem", textAlign: "center" }}>
                    <TbMail />
                    {searchVal.length > 0
                      ? t("missions.no_match_found")
                      : t("missions.no_mission")}
                  </div>
                </div>
              )}
            </TableContainer>
          </>
        </CardBox>
        {openDetails && (
          <MissionDetails
            groupData={groupData}
            userData={userData}
            open={openDetails}
            handleRequestClose={handleRequestCloseDetails}
            detailData={detailData}
            detailsLoading={detailsLoading}
          />
        )}
      </div>
    </>
  );
}
